function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function");
  }

  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      writable: true,
      configurable: true
    }
  });
  if (superClass) _setPrototypeOf(subClass, superClass);
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _createSuper(Derived) {
  var hasNativeReflectConstruct = _isNativeReflectConstruct();

  return function _createSuperInternal() {
    var Super = _getPrototypeOf(Derived),
        result;

    if (hasNativeReflectConstruct) {
      var NewTarget = _getPrototypeOf(this).constructor;

      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}

function _possibleConstructorReturn(self, call) {
  if (call && (typeof call === "object" || typeof call === "function")) {
    return call;
  } else if (call !== void 0) {
    throw new TypeError("Derived constructors may only return object or undefined");
  }

  return _assertThisInitialized(self);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

import { PureComponent } from 'react';
import PropTypes from 'prop-types'; // provides a base class for retryable error boundaries.  They will clear the error condition periodically based on
// an errorRetryMsec and maxRetryCount property.  The consumer of this should create a class component which inherits
// from this component and implement a getErrorMarkup() function that returns markup to render in the event of an error.
//
// you should also implement a getDerivedStateFromProps that will clear the error state and errorCount if something
// substantive changes about the components wrapped by this error boundary

export var RetryableErrorBoundaryBase = /*#__PURE__*/function (_PureComponent) {
  _inherits(RetryableErrorBoundaryBase, _PureComponent);

  var _super = _createSuper(RetryableErrorBoundaryBase);

  function RetryableErrorBoundaryBase() {
    var _this;

    _classCallCheck(this, RetryableErrorBoundaryBase);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty(_assertThisInitialized(_this), "state", {
      errorCount: 0
    });

    _defineProperty(_assertThisInitialized(_this), "clearErrorRetryTimeout", function () {
      if (_this.errorRetryTimeout) {
        clearTimeout(_this.errorRetryTimeout);
        delete _this.errorRetryTimeout;
      }
    });

    _defineProperty(_assertThisInitialized(_this), "startErrorRetryTimeout", function () {
      var errorRetryMsec = _this.props.errorRetryMsec;

      _this.clearErrorRetryTimeout();

      _this.errorRetryTimeout = setTimeout(function () {
        delete _this.errorRetryTimeout; // the idea is some retries will keep retrying in the background.  If error conditions still exist, we will get
        // a new exception and come right back here.

        _this.setState({
          error: null
        });
      }, errorRetryMsec);
    });

    return _this;
  }

  _createClass(RetryableErrorBoundaryBase, [{
    key: "componentDidCatch",
    value: function componentDidCatch(error) {
      var _this2 = this;

      var maxRetryCount = this.props.maxRetryCount;

      if (this.shouldCatchError && !this.shouldCatchError(error)) {
        // if we should let this error continue to percolate up the chain, then do that
        throw error;
      }

      this.setState(function (_ref) {
        var errorCount = _ref.errorCount;

        if (errorCount++ < maxRetryCount) {
          _this2.startErrorRetryTimeout();
        }

        return {
          error: error,
          errorCount: errorCount
        };
      });
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.clearErrorRetryTimeout();
    }
  }, {
    key: "render",
    value: function render() {
      var forceError = this.props.forceError;
      var stateError = this.state.error; // errors caught globally by componentDidCatch will be in stateError.  This component can also be forced into
      // an error state by providing an error to the forceError property.  That is useful if you want to trap an error
      // locally and render your own error boundary locally, rather than have this component catch the error further up
      // the DOM and prevent the component which threw the error from going back to the place where the error occurred
      // on future updates and possibly recover (this is the use case for recovering from swr errors - you need to keep
      // calling useSWR in order to see that SWR has recovered from its error state, but you can't do that unless you
      // trap the exception locally and provide your own error boundary with the error you trapped (which will be
      // provided as the forceError property), instead of relying on global trap behavior.

      var activeError = stateError || forceError;
      if (!activeError) return this.props.children;
      return this.getErrorMarkup(activeError);
    }
  }]);

  return RetryableErrorBoundaryBase;
}(PureComponent);

_defineProperty(RetryableErrorBoundaryBase, "propTypes", {
  errorRetryMsec: PropTypes.number,
  forceError: PropTypes.object,
  maxRetryCount: PropTypes.number
});

_defineProperty(RetryableErrorBoundaryBase, "defaultProps", {
  errorRetryMsec: 2000,
  maxRetryCount: 20
});

export default RetryableErrorBoundaryBase;